import { createFileRoute, Link } from '@tanstack/react-router';
import { acceptInvitation } from '@/app/services/organizationService';
import { unpackApiErr } from '@/app/utils/api';
import { queryKeys } from '@/app/utils/constants';
import { Logo } from '@/app/ui/components/logo';
import { Button } from '@/app/ui/components/button';
import { toastManager } from '@/app/ui/components/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { useNavigate } from '@tanstack/react-router';
import { validator } from '@/app/validator';
import z from 'zod';

export const Route = createFileRoute('/_authed/(membership)/accept-invitation')({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      invitationToken: z.string().optional(),
      organizationId: z.string().optional(),
      organizationName: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const searchParams = Route.useSearch();
  const navigate = useNavigate();

  const invitationToken = searchParams.invitationToken ?? '';
  const organizationId = searchParams.organizationId ?? '';
  const organizationName = searchParams.organizationName ?? '';
  const isUrlError = !invitationToken || !organizationId || !organizationName;

  const { isPending, mutate } = useMutation({
    mutationFn: () => acceptInvitation(invitationToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.IS_USER_ON_BOARD],
        refetchType: 'all',
      });
      navigate({ to: '/', search: { inviteAccepted: true, organizationName } });
    },
    onError: (error) => {
      toastManager.add({
        type: 'error',
        title: t('general.somethingWentWrong'),
        description: unpackApiErr(error) ?? t('general.notifications.error'),
      });
    },
  });

  return (
    <div className="px-5 -translate-y-5 w-full my-auto">
      <Logo className="mb-5 mx-auto" />
      {isUrlError ? (
        <div className="text-center">
          <div className="text-center pb-3">{t('pages.acceptInvitation.errors.urlError')}</div>
          <Link to="/" className="underline hover:text-foreground text-muted">
            {t('pages.acceptInvitation.links.backToHomepage')}
          </Link>
        </div>
      ) : (
        <>
          <p className="text-center font-semibold text-lg mb-0.5 lg:text-xl">
            {t.rich('pages.acceptInvitation.heading', {
              organizationName,
              b: (chunks) => <strong>{chunks}</strong>,
            })}
          </p>
          <p className="text-center text-muted text-sm mb-4">
            {t('pages.acceptInvitation.subheading')}
          </p>
          <div className="flex flex-col align-content-center">
            <Button pending={isPending} className="w-fit mx-auto" onClick={() => mutate()}>
              {t('pages.acceptInvitation.buttons.accept')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
